/**
 * Platform-aware SSO browser launcher.
 *
 * Routes the OAuth authorization URL to the right system surface:
 *   - Tauri desktop → system browser (custom-scheme deep link returns to app)
 *   - Capacitor iOS  → ASWebAuthenticationSession (intercepts the trampoline's
 *                       custom-scheme redirect; SFSafariViewController cannot)
 *   - Capacitor Android → Chrome Custom Tabs (intent filter handles the
 *                          custom-scheme redirect via App.appUrlOpen)
 *   - Web → full-page navigation
 */

import { Browser } from '@capacitor/browser';
import { isCapacitor } from '../api/config';
import {
  getClientPlatform,
  isTauriDesktop,
  openInSystemBrowser,
  runIOSWebAuth,
  WebAuthCancelledError,
} from '../utils';

const IOS_CALLBACK_SCHEME = 'ai.panluma.app';

export type LaunchResult =
  | { status: 'redirected' }
  | { status: 'completed'; inAppPath: string }
  | { status: 'cancelled' };

/**
 * Open the OAuth provider's authorization URL on the current platform.
 *
 * On iOS Capacitor this returns `{ status: 'completed', inAppPath }` —
 * the caller is responsible for navigating to that in-app SPA path so
 * SSOCallbackPage can exchange the code. On every other platform the
 * function initiates a navigation/redirect and returns `{ status: 'redirected' }`.
 */
export async function launchSSO(authUrl: string): Promise<LaunchResult> {
  if (isTauriDesktop()) {
    openInSystemBrowser(authUrl);
    return { status: 'redirected' };
  }

  if (isCapacitor && getClientPlatform() === 'ios') {
    try {
      const callbackUrl = await runIOSWebAuth(authUrl, IOS_CALLBACK_SCHEME);
      const parsed = new URL(callbackUrl);
      const inAppPath = `/auth${parsed.pathname}${parsed.search}`;
      return { status: 'completed', inAppPath };
    } catch (err) {
      if (err instanceof WebAuthCancelledError) {
        return { status: 'cancelled' };
      }
      throw err;
    }
  }

  if (isCapacitor) {
    // Android: open the auth URL in Chrome Custom Tabs and wait for either
    // the user to dismiss it (cancellation) or for the system to close it
    // because the custom-scheme intent fired (success). In the success case
    // the deep-link listener has already navigated the SPA elsewhere by the
    // time this promise resolves, so reporting "cancelled" is harmless —
    // it only resets the spinner if the user is still on the login screen.
    const finished = new Promise<void>((resolve) => {
      void Browser.addListener('browserFinished', () => resolve());
    });
    await Browser.open({ url: authUrl });
    await finished;
    return { status: 'cancelled' };
  }

  window.location.href = authUrl;
  return { status: 'redirected' };
}
